/**
 * Pricing Page - Simplified Design with Currency Detection
 * Two options: Individual Courses OR All Access Bundle
 * Shows INR for India, USD for international
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { COURSE_DATA, ALL_ACCESS, PRICING } from '../constants/courseData';
import { useCurrency } from '../hooks/useCurrency';
import { Crown, ArrowRight, Shield, Award, Sparkles, CheckCircle, Star, BookOpen, AlertCircle } from 'lucide-react';
import SEO from '../components/SEO';
import { trackPricingView } from '../utils/metaPixel';

// CDN Logo URLs
const LOGO_URLS = {
  REDHAT: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redhat/redhat-original.svg',
  LINUX: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linux/linux-original.svg',
  ANSIBLE: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ansible/ansible-original.svg',
  DOCKER: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg',
  TERRAFORM: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/terraform/terraform-original.svg',
  GIT: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg',
  PYTHON: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg',
  C_LANG: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg',
  CPP: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cplusplus/cplusplus-original.svg',
  MYSQL: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg',
  JAVA: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg',
  KUBERNETES: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg',
};

// Course logo mapping
const getCourseLogo = (code) => {
  const logoMap = {
    'RH124': LOGO_URLS.REDHAT,
    'RH134': LOGO_URLS.REDHAT,
    'SVRADM': LOGO_URLS.LINUX,
    'ANSIBLE': LOGO_URLS.ANSIBLE,
    'DOCKER': LOGO_URLS.DOCKER,
    'TERRAFORM': LOGO_URLS.TERRAFORM,
    'KUBERNETES': LOGO_URLS.KUBERNETES,
    'GIT': LOGO_URLS.GIT,
    'PYTHON': LOGO_URLS.PYTHON,
    'C_LANG': LOGO_URLS.C_LANG,
    'CPP': LOGO_URLS.CPP,
    'MYSQL': LOGO_URLS.MYSQL,
    'JAVA': LOGO_URLS.JAVA,
    'EXAM_RHCSA': LOGO_URLS.REDHAT,
    'EXAM_DO188': LOGO_URLS.REDHAT,
    'EXAM_EX294': LOGO_URLS.ANSIBLE,
  };
  return logoMap[code] || LOGO_URLS.LINUX;
};

// Course gradient colors
const getCourseGradient = (code) => {
  const gradientMap = {
    'RH124': 'from-red-500 to-rose-600',
    'RH134': 'from-red-600 to-red-700',
    'SVRADM': 'from-purple-500 to-violet-600',
    'ANSIBLE': 'from-slate-600 to-slate-800',
    'DOCKER': 'from-blue-500 to-cyan-500',
    'TERRAFORM': 'from-violet-500 to-purple-600',
    'KUBERNETES': 'from-blue-400 to-indigo-600',
    'GIT': 'from-orange-500 to-red-500',
    'PYTHON': 'from-yellow-400 to-yellow-500',
    'C_LANG': 'from-blue-400 to-blue-600',
    'CPP': 'from-indigo-500 to-purple-600',
    'MYSQL': 'from-orange-500 to-amber-500',
    'JAVA': 'from-red-500 to-orange-500',
    'EXAM_RHCSA': 'from-red-600 to-red-700',
    'EXAM_DO188': 'from-blue-600 to-cyan-600',
    'EXAM_EX294': 'from-gray-600 to-gray-800',
  };
  return gradientMap[code] || 'from-gray-500 to-gray-600';
};

// Learning paths
const DEVOPS_PATH = ['RH124', 'RH134', 'SVRADM', 'ANSIBLE', 'DOCKER', 'TERRAFORM', 'KUBERNETES', 'GIT'];
const PROGRAMMING_PATH = ['C_LANG', 'CPP', 'PYTHON', 'JAVA'];
const DATABASE_PATH = ['MYSQL'];

const Pricing = () => {
  const { isIndia, loading: currencyLoading } = useCurrency();
  const [courseAccess, setCourseAccess] = useState({});
  const [pricingInfo, setPricingInfo] = useState(null);
  const [allAccessPricing, setAllAccessPricing] = useState(null);
  const [projectsStats, setProjectsStats] = useState({ total: 0, owned: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserPricing();
    // Meta Pixel: AddToCart — pricing page is the "cart" step in our funnel.
    // Fires on every visit (intentional — repeat visitors are stronger signal).
    trackPricingView();
  }, []);

  // Fetch live projects total + owned count so the Projects card on the
  // pricing page stays dynamic as we add/remove projects in the DB.
  const fetchProjectsStats = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return { total: 0, owned: 0 };
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${apiBase}/api/projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return { total: 0, owned: 0 };
      const data = await res.json();
      return {
        total: Array.isArray(data?.projects) ? data.projects.length : 0,
        owned: data?.purchased_count || 0,
      };
    } catch {
      return { total: 0, owned: 0 };
    }
  };

  const fetchUserPricing = async () => {
    try {
      const [pricingData, allAccessData, projData] = await Promise.all([
        apiService.getUserPricingInfo().catch(() => null),
        apiService.getAllAccessPricing().catch(() => null),
        fetchProjectsStats(),
      ]);
      
      setPricingInfo(pricingData);
      setAllAccessPricing(allAccessData);
      setProjectsStats(projData);
      
      const accessMap = {};
      if (pricingData?.pricing) {
        Object.keys(pricingData.pricing).forEach(code => {
          accessMap[code] = pricingData.pricing[code]?.owned || false;
        });
      }
      if (pricingData?.has_all_access) {
        Object.keys(COURSE_DATA).forEach(code => {
          // All Access does NOT include Mock Tests or Projects Hub — keep
          // their ownership state real so the cards render correctly.
          if (code === 'MOCK_TESTS' || code === 'PROJECTS_ACCESS') return;
          accessMap[code] = true;
        });
      }
      setCourseAccess(accessMap);
    } catch (error) {
      console.error('Failed to fetch pricing info:', error);
    } finally {
      setLoading(false);
    }
  };

  // Price formatting helper
  const formatPrice = (priceINR, priceUSD) => {
    if (isIndia) {
      return `₹${priceINR.toLocaleString('en-IN')}`;
    }
    return `$${priceUSD}`;
  };

  // Key values
  const hasAllAccess = pricingInfo?.has_all_access || false;
  const ownedCount = Object.values(courseAccess).filter(Boolean).length;
  // Count ONLY core/exam courses — exclude Mock Tests + Projects Hub umbrella
  // so when we add a new course this auto-updates to 17, 18, 19 …
  const totalCourses = Object.keys(COURSE_DATA).filter(
    k => k !== 'MOCK_TESTS' && k !== 'PROJECTS_ACCESS' && !k.startsWith('EXAM_')
  ).length;
  const totalValueINR = Object.values(COURSE_DATA).reduce((sum, c) => sum + c.price, 0);
  const totalValueUSD = Object.values(COURSE_DATA).reduce((sum, c) => sum + (c.priceUSD || 19), 0);
  const mockTestsOwned = !!courseAccess['MOCK_TESTS'];
  const ownsAnyProjects = projectsStats.owned > 0;

  if (loading || currencyLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-indigo-200 border-t-indigo-600"></div>
      </div>
    );
  }

  return (
    <>
      <SEO title="Pricing" description="Linux, DevOps & Programming training pricing" />
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-4 py-1.5 rounded-full text-xs font-semibold mb-3 shadow-lg shadow-indigo-500/25">
              <Sparkles className="h-3.5 w-3.5" />
              {new Date().getFullYear()} Pricing - Simplified
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 bg-clip-text text-transparent">
              Choose Your Learning Path
            </h1>
            <p className="text-slate-500 mt-2 max-w-xl mx-auto">
              Buy individual courses or get All Access for the best value
            </p>
          </div>

          {/* All Access Already Owned */}
          {hasAllAccess && (
            <div className="mb-8 bg-gradient-to-r from-amber-400 to-orange-400 rounded-2xl p-6 shadow-xl shadow-amber-500/20" data-testid="all-access-owned-banner">
              <div className="text-center">
                <Crown className="h-12 w-12 text-white mx-auto mb-3" />
                <p className="text-white font-bold text-xl">🎉 You have All Access!</p>
                <p className="text-white/90 mt-1">
                  Lifetime access to all {totalCourses} courses
                  <span className="text-white/80"> + 3 exam attempts FREE</span>
                </p>
              </div>

              {/* Add-ons row — Mock Tests & Projects are NOT in All Access.
                  Show real ownership so users can either jump in or buy. */}
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Link
                  to="/courses"
                  data-testid="all-access-go-courses"
                  className="bg-white text-amber-700 px-4 py-2.5 rounded-lg font-semibold hover:bg-amber-50 transition-all text-sm flex items-center justify-center gap-2"
                >
                  Go to Courses <ArrowRight className="h-4 w-4" />
                </Link>
                {mockTestsOwned ? (
                  <Link
                    to="/mock-tests"
                    data-testid="all-access-open-mock-tests"
                    className="bg-emerald-600 text-white px-4 py-2.5 rounded-lg font-semibold hover:bg-emerald-700 transition-all text-sm flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="h-4 w-4" /> Open Mock Tests
                  </Link>
                ) : (
                  <Link
                    to="/payment/MOCK_TESTS"
                    data-testid="all-access-buy-mock-tests"
                    className="bg-white/15 backdrop-blur text-white border border-white/40 px-4 py-2.5 rounded-lg font-semibold hover:bg-white/25 transition-all text-sm flex items-center justify-center gap-2"
                  >
                    + Add Mock Tests · {formatPrice(499, 19)}
                  </Link>
                )}
                {ownsAnyProjects ? (
                  <Link
                    to="/projects"
                    data-testid="all-access-open-projects"
                    className="bg-emerald-600 text-white px-4 py-2.5 rounded-lg font-semibold hover:bg-emerald-700 transition-all text-sm flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="h-4 w-4" /> My Projects ({projectsStats.owned}/{projectsStats.total})
                  </Link>
                ) : (
                  <Link
                    to="/projects"
                    data-testid="all-access-browse-projects"
                    className="bg-white/15 backdrop-blur text-white border border-white/40 px-4 py-2.5 rounded-lg font-semibold hover:bg-white/25 transition-all text-sm flex items-center justify-center gap-2"
                  >
                    + Browse Projects · {formatPrice(99, 2)} each
                  </Link>
                )}
              </div>

              <p className="text-white/80 text-xs text-center mt-3 italic">
                ✦ Mock Tests &amp; Projects are sold separately to keep All Access affordable
              </p>
            </div>
          )}

          {/* All Access Card - Featured */}
          {!hasAllAccess && (
            <div className="mb-10">
              <div className="relative bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-3xl p-1 shadow-2xl shadow-amber-500/30">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-white text-amber-600 text-xs px-4 py-1.5 rounded-full font-bold shadow-lg">
                    👑 BEST VALUE - SAVE {formatPrice(totalValueINR - ALL_ACCESS.price, totalValueUSD - ALL_ACCESS.price_usd)}+
                  </span>
                </div>
                
                <div className="bg-white rounded-[22px] p-6 sm:p-8">
                  <div className="flex flex-col lg:flex-row items-center gap-6">
                    {/* Left side - Info */}
                    <div className="flex-1 text-center lg:text-left">
                      <div className="flex items-center justify-center lg:justify-start gap-3 mb-3">
                        <div className="bg-gradient-to-br from-amber-500 to-orange-500 p-3 rounded-xl">
                          <Crown className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-slate-800">All Access - Lifetime</h2>
                          <p className="text-slate-500 text-sm">One payment, lifetime access</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 my-6">
                        <div className="bg-amber-50 rounded-xl p-3 text-center">
                          <div className="text-2xl font-bold text-amber-600">{totalCourses}</div>
                          <div className="text-xs text-slate-600">Courses</div>
                        </div>
                        <div className="bg-orange-50 rounded-xl p-3 text-center">
                          <div className="text-2xl font-bold text-orange-600">{ALL_ACCESS.labs}</div>
                          <div className="text-xs text-slate-600">Labs</div>
                        </div>
                        <div className="bg-red-50 rounded-xl p-3 text-center">
                          <div className="text-2xl font-bold text-red-600">∞</div>
                          <div className="text-xs text-slate-600">Access</div>
                        </div>
                        <div className="bg-emerald-50 rounded-xl p-3 text-center">
                          <div className="text-2xl font-bold text-emerald-600">FREE</div>
                          <div className="text-xs text-slate-600">Updates</div>
                        </div>
                      </div>

                      <p className="text-slate-600 text-sm mb-3">
                        Includes: Linux (RH124, RH134), DevOps (Docker, Ansible, Kubernetes, Terraform, SVRADM, Git),
                        Programming (Python, C, C++, Core Java), Database (MySQL/MariaDB),
                        <span className="font-semibold text-emerald-700"> + 3 Exam Attempts FREE (RHCSA, DO188, EX294)</span> + all future courses
                      </p>
                      <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-left" data-testid="pricing-all-access-not-included">
                        <p className="text-rose-700 text-xs font-semibold mb-1.5 flex items-center gap-1.5">
                          <AlertCircle className="h-3.5 w-3.5" /> Not included (buy separately):
                        </p>
                        <ul className="text-xs text-slate-600 space-y-0.5 pl-0.5">
                          {isIndia ? (
                            <>
                              <li>❌ Mock Tests — ₹499</li>
                              <li>❌ Real-World Projects Hub — ₹99 per project</li>
                            </>
                          ) : (
                            <>
                              <li>❌ Mock Tests — $19</li>
                              <li>❌ Real-World Projects Hub — $2 per project</li>
                            </>
                          )}
                        </ul>
                      </div>
                    </div>
                    
                    {/* Right side - Price & CTA */}
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 text-center min-w-[280px] border border-amber-200">
                      <div className="text-slate-400 text-sm line-through">
                        {formatPrice(totalValueINR, totalValueUSD)} value
                      </div>
                      
                      {/* Show credit if applicable, with hover breakdown */}
                      {allAccessPricing?.credit > 0 && isIndia && (
                        <div className="relative group/credit inline-block my-1" data-testid="credit-hover-wrapper">
                          <div className="text-emerald-600 text-sm font-medium cursor-help border-b border-dashed border-emerald-400">
                            Your Credit: -₹{allAccessPricing.credit.toLocaleString()} <span className="text-[10px] text-emerald-500">ⓘ</span>
                          </div>
                          {allAccessPricing.credited_items && allAccessPricing.credited_items.length > 0 && (
                            <div
                              data-testid="credit-breakdown-tooltip"
                              className="invisible opacity-0 group-hover/credit:visible group-hover/credit:opacity-100 transition-opacity absolute z-30 left-1/2 -translate-x-1/2 mt-2 w-64 bg-slate-900 text-white rounded-lg shadow-2xl p-3 text-left"
                            >
                              <div className="text-[11px] uppercase tracking-wider text-emerald-300 font-bold mb-2">Credit Breakdown</div>
                              <ul className="space-y-1">
                                {allAccessPricing.credited_items.map((it, i) => (
                                  <li key={i} className="flex justify-between text-xs">
                                    <span className="text-slate-300 truncate mr-2">{it.item}</span>
                                    <span className="text-emerald-400 font-semibold whitespace-nowrap">₹{(it.inr || it.paise / 100).toLocaleString()}</span>
                                  </li>
                                ))}
                              </ul>
                              <div className="border-t border-slate-700 mt-2 pt-2 flex justify-between text-xs font-bold">
                                <span>Total Credit</span>
                                <span className="text-emerald-300">₹{allAccessPricing.credit.toLocaleString()}</span>
                              </div>
                              <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-slate-900 rotate-45"></div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="text-4xl font-bold text-slate-800 my-2">
                        {isIndia 
                          ? `₹${(allAccessPricing?.final_price || ALL_ACCESS.price).toLocaleString()}`
                          : `$${ALL_ACCESS.price_usd}`
                        }
                      </div>
                      <div className="text-slate-500 text-sm mb-4">
                        One-time payment • Lifetime access
                      </div>
                      
                      <Link 
                        to="/payment/ALL_ACCESS"
                        data-testid="all-access-buy-button"
                        className="block w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-4 rounded-xl font-bold text-lg transition-all shadow-lg shadow-amber-500/25 hover:shadow-xl hover:-translate-y-0.5"
                      >
                        Get All Access
                      </Link>
                      
                      <p className="text-xs text-slate-400 mt-3">
                        30-day money-back guarantee
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Course Category Tabs */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-indigo-600" />
              Individual Courses
            </h3>
          </div>

          {/* ===== "Save with All Access" SMART CTA BANNER ===== */}
          {!hasAllAccess && (() => {
            const finalPrice = allAccessPricing?.final_price || 4999;
            const basePrice = allAccessPricing?.base_price || 4999;
            const credit = allAccessPricing?.credit || 0;
            const remainingCourses = allAccessPricing?.remaining_courses_only ?? 13;
            const remainingExams = allAccessPricing?.remaining_exams_only ?? 3;
            // Credit-branch applies to India only (backend doesn't credit international users)
            const showCreditBranch = credit > 0 && isIndia;
            return (
              <div
                data-testid="all-access-cta-banner"
                className="relative overflow-hidden rounded-2xl mb-8 bg-gradient-to-br from-amber-500 via-orange-500 to-rose-600 shadow-lg"
              >
                {/* decorative ring */}
                <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-white/10 pointer-events-none"></div>
                <div className="absolute -left-10 -bottom-10 w-40 h-40 rounded-full bg-white/5 pointer-events-none"></div>

                <div className="relative flex flex-col lg:flex-row items-center gap-5 p-5 lg:p-6">
                  <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
                    <Crown className="w-8 h-8 text-white drop-shadow" />
                  </div>

                  <div className="flex-1 text-center lg:text-left text-white">
                    <div className="text-[11px] uppercase tracking-[0.2em] font-bold opacity-90 mb-0.5">💡 Smart Pick</div>
                    <div className="text-lg lg:text-xl font-extrabold leading-tight">
                      {showCreditBranch ? (
                        <>Already spent ₹{credit.toLocaleString()} on courses? Upgrade to <span className="underline decoration-yellow-200 decoration-4 underline-offset-2">All Access</span> for just <span className="text-yellow-200">₹{finalPrice.toLocaleString()}</span> more!</>
                      ) : (
                        <>Stop buying one-by-one — unlock <span className="underline decoration-yellow-200 decoration-4 underline-offset-2">everything</span> for {isIndia ? `₹${basePrice.toLocaleString()}` : `$${ALL_ACCESS.price_usd}`}</>
                      )}
                    </div>
                    <div className="text-sm opacity-95 mt-1">
                      {showCreditBranch ? (
                        <>✓ Your ₹{credit.toLocaleString()} from past purchases is already discounted
                          {remainingCourses > 0 && <> · ✓ {remainingCourses} more {remainingCourses === 1 ? 'course' : 'courses'} unlock instantly</>}
                          {remainingExams > 0 && <> · ✓ {remainingExams} exam {remainingExams === 1 ? 'attempt' : 'attempts'} FREE</>}
                        </>
                      ) : (
                        <>
                          {(() => {
                            const displayCourses = remainingCourses ?? 13;
                            const displayExams = remainingExams ?? 3;
                            return (
                              <>
                                {displayCourses > 0 && <>✓ {displayCourses} {displayCourses === 1 ? 'course' : 'courses'}</>}
                                {displayCourses > 0 && displayExams > 0 && ' · '}
                                {displayExams > 0 && <>✓ {displayExams} Exam {displayExams === 1 ? 'Attempt' : 'Attempts'} FREE (RHCSA, DO188, EX294)</>}
                                {(displayCourses > 0 || displayExams > 0) && ' · '}
                                ✓ 249 hands-on labs · ✓ Lifetime access
                              </>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  </div>

                  <Link
                    to="/payment/ALL_ACCESS"
                    data-testid="cta-banner-upgrade"
                    className="flex-shrink-0 bg-white text-orange-600 font-bold px-6 py-3 rounded-xl hover:bg-yellow-50 hover:scale-105 transition-all shadow-xl flex items-center gap-2 whitespace-nowrap"
                  >
                    {showCreditBranch ? `Pay Only ₹${finalPrice.toLocaleString()}` : 'Get All Access'}
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            );
          })()}

          {/* Price Tiers Legend */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg shadow-sm border border-slate-100">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span className="text-sm text-slate-600">Linux - {formatPrice(999, 45)}</span>
            </div>
            <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg shadow-sm border border-slate-100">
              <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
              <span className="text-sm text-slate-600">DevOps - {formatPrice(799, 35)}</span>
            </div>
            <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg shadow-sm border border-slate-100">
              <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
              <span className="text-sm text-slate-600">Basic/Programming - {formatPrice(499, 19)}</span>
            </div>
            <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg shadow-sm border border-slate-100">
              <div className="w-3 h-3 bg-orange-400 rounded-full"></div>
              <span className="text-sm text-slate-600">Database - {formatPrice(499, 19)}</span>
            </div>
          </div>

          {/* ======= SECTION 1: COURSES ======= */}
          {(() => {
            const courseKeys = Object.keys(COURSE_DATA).filter(k => !k.startsWith('EXAM_') && k !== 'MOCK_TESTS' && k !== 'PROJECTS_ACCESS');
            const examKeys = Object.keys(COURSE_DATA).filter(k => k.startsWith('EXAM_'));
            const renderCard = (code) => {
              const course = COURSE_DATA[code];
              if (!course) return null;
              const isOwned = courseAccess[code] || hasAllAccess;
              const gradient = getCourseGradient(code);
              const displayPrice = formatPrice(course.price, course.priceUSD || 19);
              return (
                <div
                  key={code}
                  className={`group bg-white rounded-xl p-4 shadow-md hover:shadow-xl transition-all duration-300 border-2 ${
                    isOwned ? 'border-emerald-400' : 'border-transparent hover:border-slate-200'
                  }`}
                  data-testid={`course-card-${code}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center shadow-sm p-2`}>
                      <img src={getCourseLogo(code)} alt={course.name} className="w-6 h-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-slate-800 truncate">{code}</div>
                      <div className="text-xs text-slate-400">{course.labs} Labs</div>
                    </div>
                  </div>
                  {isOwned ? (
                    <Link
                      to={`/courses/${code}`}
                      className="block text-center bg-emerald-50 text-emerald-600 py-2 rounded-lg text-sm font-semibold hover:bg-emerald-100 transition-all"
                    >
                      <CheckCircle className="inline h-4 w-4 mr-1" />
                      View Labs
                    </Link>
                  ) : (
                    <Link
                      to={`/payment/${code}`}
                      data-testid={`buy-${code}`}
                      className={`block text-center bg-gradient-to-r ${gradient} text-white py-2 rounded-lg text-sm font-semibold transition-all shadow-md group-hover:shadow-lg`}
                    >
                      {displayPrice}
                    </Link>
                  )}
                </div>
              );
            };
            return (
              <>
                <div className="mb-10" data-testid="individual-courses-section">
                  <div className="relative flex items-center gap-4 mb-5 p-4 bg-white rounded-xl shadow-sm border-l-4 border-indigo-500">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
                      <BookOpen className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-lg font-extrabold text-slate-900 tracking-tight">Core Courses</h4>
                      <p className="text-sm text-slate-600 mt-0.5 font-medium">Hands-on labs · Industry-standard curriculum</p>
                    </div>
                    <span className="flex-shrink-0 text-sm font-bold px-3.5 py-1.5 rounded-full bg-indigo-600 text-white shadow-md">{courseKeys.length} courses</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {courseKeys.map(renderCard)}
                  </div>
                </div>

                {examKeys.length > 0 && (
                  <div className="mb-10" data-testid="individual-exams-section">
                    <div className="relative flex items-center gap-4 mb-5 p-4 bg-white rounded-xl shadow-sm border-l-4 border-rose-500">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center shadow-lg shadow-rose-500/30">
                        <Award className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-lg font-extrabold text-slate-900 tracking-tight">Certification Exam Attempts</h4>
                        <p className="text-sm text-slate-600 mt-0.5 font-medium">Real exam environment · 1 attempt per purchase</p>
                      </div>
                      <span className="flex-shrink-0 text-sm font-bold px-3.5 py-1.5 rounded-full bg-rose-600 text-white shadow-md">{examKeys.length} exams</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                      {examKeys.map(renderCard)}
                    </div>
                  </div>
                )}

                <div className="mb-10" data-testid="individual-extras-section">
                  <div className="relative flex items-center gap-4 mb-5 p-4 bg-white rounded-xl shadow-sm border-l-4 border-emerald-500">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-lg font-extrabold text-slate-900 tracking-tight">Practice & Projects</h4>
                      <p className="text-sm text-slate-600 mt-0.5 font-medium">Sharpen skills · Build portfolio · Sold separately</p>
                    </div>
                    <span className="flex-shrink-0 text-sm font-bold px-3.5 py-1.5 rounded-full bg-emerald-600 text-white shadow-md">2 add-ons</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {/* Mock Tests Card */}
                    <div
                      className={`group bg-white rounded-xl p-4 shadow-md hover:shadow-xl transition-all duration-300 border-2 ${
                        mockTestsOwned ? 'border-emerald-400' : 'border-transparent hover:border-slate-200'
                      }`}
                      data-testid="card-mock-tests"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-sm">
                          <Award className="h-5 w-5 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-800 truncate">Mock Tests</div>
                          <div className="text-xs text-slate-400">30+ Tests</div>
                        </div>
                      </div>
                      {mockTestsOwned ? (
                        <Link
                          to="/mock-tests"
                          data-testid="open-mock-tests"
                          className="block text-center bg-emerald-50 text-emerald-600 py-2 rounded-lg text-sm font-semibold hover:bg-emerald-100 transition-all"
                        >
                          <CheckCircle className="inline h-4 w-4 mr-1" />
                          Open Mock Tests
                        </Link>
                      ) : (
                        <Link
                          to="/payment/MOCK_TESTS"
                          data-testid="buy-mock-tests"
                          className="block text-center bg-gradient-to-r from-emerald-500 to-green-600 text-white py-2 rounded-lg text-sm font-semibold transition-all shadow-md group-hover:shadow-lg"
                        >
                          {formatPrice(499, 19)}
                        </Link>
                      )}
                    </div>
                    {/* Projects Hub Card */}
                    <div
                      className={`group bg-white rounded-xl p-4 shadow-md hover:shadow-xl transition-all duration-300 border-2 ${
                        ownsAnyProjects ? 'border-emerald-400' : 'border-transparent hover:border-slate-200'
                      }`}
                      data-testid="card-projects-hub"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-sm">
                          <Sparkles className="h-5 w-5 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-slate-800 truncate">Projects Hub</div>
                          <div className="text-xs text-slate-400">
                            {projectsStats.total > 0
                              ? `${projectsStats.total} Real-World ${projectsStats.total === 1 ? 'Project' : 'Projects'}`
                              : 'Real-World Projects'}
                          </div>
                        </div>
                      </div>
                      {ownsAnyProjects ? (
                        <Link
                          to="/projects"
                          data-testid="open-projects"
                          className="block text-center bg-emerald-50 text-emerald-600 py-2 rounded-lg text-sm font-semibold hover:bg-emerald-100 transition-all"
                        >
                          <CheckCircle className="inline h-4 w-4 mr-1" />
                          {projectsStats.owned} of {projectsStats.total} owned
                        </Link>
                      ) : (
                        <Link
                          to="/projects"
                          data-testid="buy-projects"
                          className="block text-center bg-gradient-to-r from-amber-500 to-orange-600 text-white py-2 rounded-lg text-sm font-semibold transition-all shadow-md group-hover:shadow-lg"
                        >
                          {formatPrice(99, 2)} / project
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </>
            );
          })()}

          {/* Learning Paths */}
          <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 rounded-2xl p-5 mb-8 border border-indigo-100/50">
            <h4 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
              <span className="bg-indigo-100 p-1.5 rounded-lg"><Sparkles className="h-4 w-4 text-indigo-600" /></span>
              Recommended Learning Paths
            </h4>
            
            {/* DevOps Path */}
            <div className="mb-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Linux & DevOps Track</p>
              <div className="flex flex-wrap items-center gap-1.5">
                {DEVOPS_PATH.map((step, index) => (
                  <React.Fragment key={step}>
                    <span className={`bg-white px-2.5 py-1 rounded-lg text-xs font-medium shadow-sm border ${
                      courseAccess[step] ? 'border-emerald-200 text-emerald-700 bg-emerald-50' : 'border-slate-100 text-slate-700'
                    }`}>
                      {courseAccess[step] && <CheckCircle className="inline h-3 w-3 mr-1" />}
                      {index + 1}. {step}
                    </span>
                    {index < DEVOPS_PATH.length - 1 && (
                      <ArrowRight className="h-3 w-3 text-indigo-400 flex-shrink-0" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            
            {/* Programming Path */}
            <div className="mb-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Programming Track</p>
              <div className="flex flex-wrap items-center gap-2">
                {PROGRAMMING_PATH.map((step, index) => (
                  <React.Fragment key={step}>
                    <span className={`bg-white px-3 py-1.5 rounded-lg text-sm font-medium shadow-sm border ${
                      courseAccess[step] ? 'border-emerald-200 text-emerald-700 bg-emerald-50' : 'border-emerald-100 text-slate-700'
                    }`}>
                      {courseAccess[step] && <CheckCircle className="inline h-3 w-3 mr-1" />}
                      {index + 1}. {step === 'C_LANG' ? 'C' : step === 'JAVA' ? 'Core Java' : step}
                    </span>
                    {index < PROGRAMMING_PATH.length - 1 && (
                      <ArrowRight className="h-4 w-4 text-emerald-400" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Database Path */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Database Track</p>
              <div className="flex flex-wrap items-center gap-2">
                {DATABASE_PATH.map((step, index) => (
                  <React.Fragment key={step}>
                    <span className={`bg-white px-3 py-1.5 rounded-lg text-sm font-medium shadow-sm border ${
                      courseAccess[step] ? 'border-emerald-200 text-emerald-700 bg-emerald-50' : 'border-orange-100 text-slate-700'
                    }`}>
                      {courseAccess[step] && <CheckCircle className="inline h-3 w-3 mr-1" />}
                      {index + 1}. MySQL/MariaDB
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {/* Projects Section */}
          <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-6 shadow-sm border border-orange-100" data-testid="pricing-projects-section">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="bg-gradient-to-br from-orange-500 to-red-500 p-3 rounded-xl shadow-lg">
                  <BookOpen className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-800">Real-World Projects</h3>
                  <p className="text-sm text-slate-500">12 hands-on projects across Linux, Docker, AWS, Terraform, Kubernetes, C, C++ & more</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <span className="text-2xl font-bold text-orange-600">{isIndia ? '₹99' : '$2'}</span>
                  <span className="text-sm text-slate-400 ml-1">/ project</span>
                </div>
                <Link to="/projects" className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:shadow-lg hover:shadow-orange-200 transition-all">
                  Browse Projects <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-5 shadow-sm border border-slate-100">
            <div className="flex flex-wrap justify-center gap-8">
              <div className="flex items-center gap-2.5">
                <div className="bg-emerald-100 p-2.5 rounded-xl">
                  <Shield className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <div className="font-semibold text-slate-700 text-sm">Secure Payment</div>
                  <div className="text-xs text-slate-400">Razorpay Protected</div>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="bg-blue-100 p-2.5 rounded-xl">
                  <Award className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <div className="font-semibold text-slate-700 text-sm">Certificate</div>
                  <div className="text-xs text-slate-400">Industry Recognized</div>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="bg-amber-100 p-2.5 rounded-xl">
                  <Star className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <div className="font-semibold text-slate-700 text-sm">Lifetime Access</div>
                  <div className="text-xs text-slate-400">Learn at your pace</div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default Pricing;
